/// <amd-module name="Repos/VersionDropdown/VersionDropdownProvider" />
import { Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import { ArtifactDropdownProviderBase, IArtifactDropdownProviderOptions, IFavoritesContext } from "Favorites/Dropdown/ArtifactDropdownProvider";
import * as React from "react";
import { GitCommitRef, GitRef, GitRepository, GitVersionDescriptor, GitVersionOptions, GitVersionType } from "Repos/Common/Contracts/Git";
import * as RefUtils from "Repos/Common/Util/Ref";
import { getVersionString } from "Repos/Common/Util/Version";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IVersionDropdownItem, IVersionDropdownOptions } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import { GitRefFavoriteArtifactConstants, GitRefFavoritesService } from "Repos/VersionDropdown/GitRefFavoritesService";
import * as Resources from "Repos/VersionDropdown/Resources";
import { getGitClient } from "Repos/VersionDropdown/RestClient/Git";
import { IReadyableReadonlyObservableArray, ObservableValue, ReadyableObservableArray } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { IButtonProps } from "VSSUI/Button";
import { IDropdownPivot } from "VSSUI/Dropdown";
import { IListSelection } from "VSSUI/List";
import { IListBoxGroup, IListBoxItem, ListBoxItemType, LoadingCell } from "VSSUI/ListBox";
import { ITableColumn } from "VSSUI/Table";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";
import { IGitRefService, PagedResults } from "./GitRefService";

interface PrefixAndLastName {
    prefix?: string;
    lastName: string;
}

const enum GroupIds {
    repositoryDefault = "default",
    mineBranches = "favorites",
    allBranches = "all-branches",
    allTags = "all-tags",
    foundCommits = "found-commits"
}

export const enum PivotIds {
    branches = "branches",
    tags = "tags",
    commits = "commits"
}

type SearchItemsType = (searchText: string, continuationToken?: string) => PagedResults<IVersionDropdownItem>;

/**
 * This is a special token that will return every ref ("!" is before every "ref/*" name)
 * while it allows us to declare the mine refs as a PagedList with continuation.
 */
const mineContinuationToken = "!";

export class VersionDropdownProvider extends ArtifactDropdownProviderBase<IVersionDropdownItem> {
    private artifactPickerOptions: IArtifactDropdownProviderOptions<IVersionDropdownItem>;
    private gitRefFavoritesService: GitRefFavoritesService;
    private continuationTokens: { [groupKey: string]: string | null | undefined } = {};
    private pivots: IDropdownPivot<IVersionDropdownItem>[];
    private groupToPivotMap: { [groupId: string]: string } = {};
    private currentIdentityId = "";
    private viewMyBranches: boolean;
    private viewTagsPivot: boolean;
    private repository: GitRepository;
    private commitsItemProvider: GroupedItemProvider<IVersionDropdownItem>;
    private commitsFilterText: string = "";
    private timeout: number | undefined;
    private lastPivot: string;
    private lastFilterText: string;
    private searchFunctionsByPivot: { [pivotKey: string]: SearchItemsType };

    constructor(
        private pageContext: IVssPageContext,
        private versionDropdownOptions: IVersionDropdownOptions,
        private selection: IListSelection,
        private searching: ObservableValue<boolean>,
        private loading: ObservableValue<boolean>,
        private renderAllBranchesPicker?: boolean,
        private onSelect?: (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem> | null) => void
    ) {
        super();

        this.repository = !!versionDropdownOptions.repository ? versionDropdownOptions.repository : getGitRepository(pageContext)!;

        // These values may not be valid for the standard favorites service, but we'll consume them in the GitRefFavoritesService instead.
        const favoritesContext: IFavoritesContext = {
            artifactType: GitRefFavoriteArtifactConstants.artifactType,
            artifactScope: {
                id: this.repository.id,
                name: this.repository.name,
                type: GitRefFavoriteArtifactConstants.artifactScopeType
            }
        };

        const iconByKind = {
            [GitVersionType.Branch]: "OpenSource",
            [GitVersionType.Tag]: "Tag",
            [GitVersionType.Commit]: "BranchCommit"
        };

        const trim = (fn: SearchItemsType) => (searchText: string, continuationToken: string) =>
            fn(searchText && searchText.trim(), continuationToken);

        const emptyItems = new ReadyableObservableArray<IVersionDropdownItem>();
        emptyItems.ready.value = true;

        const abortIfBranchesFullyLoaded = (fn: SearchItemsType) => (searchText: string, continuationToken: string) =>
            this.isGroupFullyLoaded(GroupIds.allBranches) ? { items: emptyItems } : fn(searchText, continuationToken);

        const abortIfTagsFullyLoaded = (fn: SearchItemsType) => (searchText: string, continuationToken: string) =>
            this.isGroupFullyLoaded(GroupIds.allTags) ? { items: emptyItems } : fn(searchText, continuationToken);

        const reuseLastRefs = (fn: SearchItemsType) => this.reuseIfStrictlyStronger(fn, areRefsResultsComplete);

        this.searchFunctionsByPivot = {
            [PivotIds.branches]: abortIfBranchesFullyLoaded(trim(reuseLastRefs(this.searchBranchRefs))),
            [PivotIds.tags]: abortIfTagsFullyLoaded(trim(reuseLastRefs(this.searchTagRefs)))
        };

        this.viewMyBranches = versionDropdownOptions.viewMyBranches === undefined ? true : !!versionDropdownOptions.viewMyBranches;
        this.viewTagsPivot = versionDropdownOptions.viewTagsPivot === undefined ? true : !!versionDropdownOptions.viewTagsPivot;

        this.groupToPivotMap = createGroupToPivotMap();
        const groups = this.createGroups(versionDropdownOptions, {
            [GroupIds.allBranches]: true,
            [GroupIds.allTags]: true,
            [GroupIds.foundCommits]: false
        });
        this.pivots = this.createPivots(versionDropdownOptions, groups, this.groupToPivotMap);

        const vssPageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        if (vssPageData) {
            this.currentIdentityId = vssPageData.user.id;
        }

        const { id: projectId } = this.repository.project;
        this.gitRefFavoritesService = this.getGitRefFavoritesService(projectId, this.viewMyBranches);
        this.artifactPickerOptions = {
            pageContext: pageContext,
            favoritesContext,
            favoritesService: this.gitRefFavoritesService,
            getArtifacts: this.getDropdownItems,
            getArtifactId: item => getVersionString(item.versionDescriptor),
            getArtifactName: item => item.friendlyName,
            getArtifactIcon: item => ({
                iconName: iconByKind[item.versionDescriptor.versionType]
            }),
            getArtifactTextNode: renderItemTextNode,
            getFavoriteFromArtifact,
            getArtifactListGroupId,
            groups,
            pivots: this.pivots,
            groupToPivotMap: this.groupToPivotMap,
            hideFavoriteSelectedItemIndicator: versionDropdownOptions.hideFavoriteSelectedItemIndicator
        };

        this.initialize(this.artifactPickerOptions);

        // Tags are not cached, so we only need to notify of changes in branches.
        this.getGitRefService().registerBranchesCacheListener(this.forgetCachedArtifacts);
    }

    public getPivots(): IDropdownPivot<IVersionDropdownItem>[] {
        this.pivots[0].actions = this.getBranchesActions();
        return this.pivots;
    }

    public dispose(): void {
        super.dispose();

        this.getGitRefService().unregisterBranchesCacheListener(this.forgetCachedArtifacts);
    }

    public setSearchLoading = (filterText: string, loading: boolean, selectedPivot: string) => {
        this.setCommitLoading(filterText, loading);
        const groupIds = Object.keys(this.continuationTokens);
        const pivot = this.pivots.find(pivot => pivot.id === selectedPivot);
        if (loading && filterText && this.searchFunctionsByPivot[selectedPivot || PivotIds.branches]) {
            if (!this.searching.value) {
                this.searching.value = true;
            }

            if (!this.lastFilterText) {
                // remove current loading items
                groupIds.forEach(groupId => {
                    if (this.continuationTokens[groupId]) {
                        const items = this.getItemsForGroup(groupId);
                        if (items) {
                            items.setGroupLoading(groupId, false);
                        }
                    }
                });
            }

            if (pivot) {
                const delayBeforeOnlineSearchMs = 1000;
                this.debounceFunction(() => {
                    this.removeSearchLoadingItem(pivot);
                    const items = pivot.items as GroupedItemProvider<IVersionDropdownItem>;
                    const loadingItem = this.getSearchLoadingItem(filterText, selectedPivot, "");
                    items.push(loadingItem);
                }, delayBeforeOnlineSearchMs);
            }
        } else {
            if (this.searching.value) {
                this.searching.value = false;
            }

            if (this.timeout) {
                clearTimeout(this.timeout);
            }

            // remove the search loading item
            if (pivot) {
                this.removeSearchLoadingItem(pivot);
            }

            if (this.lastFilterText) {
                // add back any normal loading items
                groupIds.forEach(groupId => {
                    if (this.continuationTokens[groupId]) {
                        const items = this.getItemsForGroup(groupId);
                        if (items) {
                            items.setGroupLoading(groupId, true, this.getLoadingItem(groupId));
                        }
                    }
                });
            }
        }
        this.lastPivot = selectedPivot;
        this.lastFilterText = filterText;
    };

    public updatePivot(selectedPivot: string) {
        if (this.lastPivot && this.lastFilterText) {
            // remove loading from the last pivot
            const pivot = this.pivots.find(pivot => pivot.id === this.lastPivot);
            if (pivot) {
                const items = pivot.items as GroupedItemProvider<IVersionDropdownItem>;
                const loadingIndex = items.value.findIndex(item => item.id === this.getSearchLoadingItemId(selectedPivot));
                if (loadingIndex > -1) {
                    items.splice(loadingIndex, 1);
                }
            }
            // add a loading item to the new pivot
            this.setSearchLoading(this.lastFilterText, true, selectedPivot);
        }
    }

    private removeSearchLoadingItem = (pivot: IDropdownPivot<IVersionDropdownItem>) => {
        const items = pivot.items as GroupedItemProvider<IVersionDropdownItem>;
        const currentLoadingIndex = items.value.findIndex(item => item.id === this.getSearchLoadingItemId(pivot.id));
        if (currentLoadingIndex > -1) {
            items.splice(currentLoadingIndex, 1);
        }
    };

    private setCommitLoading = (filterText: string, loading: boolean) => {
        const commitsPivot = this.pivots.find(item => item.id === PivotIds.commits);

        if (commitsPivot) {
            this.commitsFilterText = filterText;
            if (filterText.length > 0) {
                if (this.commitsItemProvider.length) {
                    this.commitsItemProvider.removeAll();
                    this.forgetCachedArtifacts(PivotIds.commits);
                }
                if (filterText.length > 3) {
                    this.commitsItemProvider.setGroupLoading(GroupIds.foundCommits, loading, this.getLoadingItem(GroupIds.foundCommits));
                    (commitsPivot.filteredNoResultsText as ObservableValue<string>).value = Resources.CommitsSearchNoResultsText;
                } else {
                    (commitsPivot.filteredNoResultsText as ObservableValue<string>).value = Resources.CommitsSearchMinimumCharactersError;
                }
            }
        }
    };

    private getDropdownItems = (groupKey?: string): IReadyableReadonlyObservableArray<IVersionDropdownItem> => {
        if (!groupKey) {
            // This provider relies on onGroupLoadRequest even for initial load
            const items = new ReadyableObservableArray<IVersionDropdownItem>();
            items.ready.value = true;
            return items;
        }

        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.startScenario("branches-dropdown-get-dropdown-items", false);

        const refs: PagedResults<GitRef | GitCommitRef> = this.getVersions(groupKey);
        const items = new ReadyableObservableArray<IVersionDropdownItem>();

        const process = () => {
            this.continuationTokens[groupKey] = refs.continuationToken;

            const itemsForGroup = this.getItemsForGroup(groupKey);
            if (itemsForGroup) {
                itemsForGroup.setGroupLoading(groupKey, false);
            }

            const process = (folderFavorites: string[]) => {
                items.push(
                    ...refs.items.value.map(ref => createVersionDropdownItem(ref, groupKey, this.repository, this.currentIdentityId, folderFavorites))
                );
                items.ready.value = true;

                performanceService.endScenario("Repos", "branches-dropdown-get-dropdown-items", false, undefined, {
                    groupKey,
                    count: items.length,
                    hasMore: Boolean(refs.continuationToken)
                });
            };

            // This assumes ArtifactPickerProvider has already called getFavorites, otherwise getCurrentFolderFavorites would throw
            if (this.viewMyBranches && this.isAuthorizedUser()) {
                const folderFavorites = this.gitRefFavoritesService.getCurrentFolderFavorites();
                this.addObserver(folderFavorites.ready, (value: boolean) => {
                    if (value) {
                        process(folderFavorites.value);
                    }
                });
            } else {
                process([]);
            }
        };

        if (!refs.items.ready.value) {
            this.addObserver(refs.items.ready, value => {
                if (value) {
                    process();
                }
            });
        } else {
            process();
        }

        this.renderAllBranchesPicker && items.push(createAllBranchesVersionDropdownItem(this.repository));

        return items;
    };

    private getVersions(groupKey: string): PagedResults<GitRef | GitCommitRef> {
        const continuationToken = this.continuationTokens[groupKey];
        const {
            id: repositoryId,
            project: { id: projectId }
        } = this.repository;
        if (groupKey === GroupIds.foundCommits) {
            const {
                id: repositoryId,
                project: { id: projectId }
            } = this.repository;
            return this.getGitRefService().getCommitsById(repositoryId, projectId, this.commitsFilterText);
        }

        if (continuationToken) {
            return groupKey === GroupIds.allTags
                ? this.getGitRefService().getMoreTags(repositoryId, projectId, continuationToken)
                : this.getGitRefService().getMoreBranches(repositoryId, projectId, continuationToken);
        } else {
            return groupKey === GroupIds.allTags
                ? this.getGitRefService().getPagedTags(repositoryId, projectId)
                : this.viewMyBranches
                ? this.getMyBranches()
                : this.getGitRefService().getPagedBranches(repositoryId, projectId);
        }
    }

    private getMyBranches(): PagedResults<GitRef> {
        const {
            id: repositoryId,
            project: { id: projectId }
        } = this.repository;
        const items = this.getGitRefService().getMyBranches(repositoryId, projectId);
        return { items, continuationToken: mineContinuationToken };
    }

    private getGitRefFavoritesService(projectId: string, viewMine: boolean): GitRefFavoritesService {
        const standardFavoritesService = this.pageContext.getService<IFavoritesService>("IFavoritesService");
        const gitRestClient = getGitClient(this.pageContext);
        return new GitRefFavoritesService(gitRestClient, viewMine && standardFavoritesService.canUseFavorites(), projectId);
    }

    private getGitRefService(): IGitRefService {
        return this.pageContext.getService<IGitRefService>("IGitRefService");
    }

    private getGroupsLoadingState(): { [groupKey: string]: boolean } {
        if (this.artifactPickerOptions && this.artifactPickerOptions.groups) {
            return this.artifactPickerOptions.groups.reduce(
                (state, group) => ({
                    ...state,
                    [group.id]: group.loading || this.continuationTokens[group.id]
                }),
                {}
            );
        } else {
            return {};
        }
    }

    private isGroupFullyLoaded(groupKey: GroupIds): boolean {
        return !this.getGroupsLoadingState()[groupKey];
    }

    private createGroups(
        { viewCommitsPivot }: IVersionDropdownOptions,
        loadingState: { [groupKey: string]: boolean }
    ): IListBoxGroup<IVersionDropdownItem>[] {
        return [
            {
                id: GroupIds.repositoryDefault,
                name: undefined
            },
            this.viewMyBranches && {
                id: GroupIds.mineBranches,
                name: Resources.MineBranches
            },
            {
                id: GroupIds.allBranches,
                name: Resources.AllBranches,
                loading: loadingState[GroupIds.allBranches],
                loadingItem: this.getLoadingItem(GroupIds.allBranches)
            },
            this.viewTagsPivot && {
                id: GroupIds.allTags,
                name: undefined,
                loading: loadingState[GroupIds.allTags],
                loadingItem: this.getLoadingItem(GroupIds.allTags)
            },
            viewCommitsPivot && {
                id: GroupIds.foundCommits,
                name: undefined,
                loading: loadingState[GroupIds.foundCommits],
                loadingItem: this.getLoadingItem(GroupIds.foundCommits)
            }
        ].filter(Boolean) as IListBoxGroup<IVersionDropdownItem>[];
    }

    private processLoadedItems(items: GroupedItemProvider<IVersionDropdownItem> | undefined, results: IListBoxItem<IVersionDropdownItem>[]) {
        if (items) {
            const newItems: IListBoxItem<IVersionDropdownItem>[] = [...results];
            const mineId = "mine-header";
            const allId = "all-header";
            if (results.some(item => item.groupId === GroupIds.mineBranches) && !items.value.some(item => item.id === mineId)) {
                newItems.push({
                    id: mineId,
                    type: ListBoxItemType.Header,
                    text: Resources.MineBranches,
                    groupId: GroupIds.mineBranches
                });
            }
            if (results.some(item => item.groupId === GroupIds.allBranches) && !items.value.some(item => item.id === allId)) {
                newItems.push({
                    id: allId,
                    type: ListBoxItemType.Header,
                    text: Resources.AllBranches,
                    groupId: GroupIds.allBranches
                });
            }
            items.push(...newItems);
        }
    }

    private getLoadingItem(groupId: string): IListBoxItem<IVersionDropdownItem> {
        return {
            id: groupId + "-loading-item",
            groupId: groupId,
            type: ListBoxItemType.Loading,
            render: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IListBoxItem<{}>>, tableItem: IListBoxItem<{}>) => {
                return (
                    <LoadingCell
                        key={tableItem.id}
                        columnIndex={columnIndex}
                        tableColumn={tableColumn}
                        tableItem={tableItem}
                        onMount={() => {
                            if (!this.loading.value) {
                                this.loading.value = true;
                            }
                            const process = (results: IListBoxItem<IVersionDropdownItem>[]) => {
                                const items = this.getItemsForGroup(groupId);
                                this.processLoadedItems(items, results);
                                if (this.continuationTokens[groupId] && items && !this.lastFilterText) {
                                    items.setGroupLoading(groupId, true, this.getLoadingItem(groupId));
                                } else if (this.loading.value) {
                                    this.loading.value = false;
                                }
                            };

                            const results = this.onGroupLoadRequest(groupId);
                            if (!results.ready.value) {
                                const observer = (value: boolean) => {
                                    if (value) {
                                        process(results.value);
                                    }
                                };
                                this.addObserver(results.ready, observer);
                            } else {
                                process(results.value);
                            }
                        }}
                    />
                );
            }
        };
    }

    private getSearchLoadingItemId(pivotId: string): string {
        return pivotId + "-search-loading-item";
    }

    private getSearchLoadingItem(filterText: string, pivotId: string, continuationToken: string): IListBoxItem<IVersionDropdownItem> {
        return {
            id: this.getSearchLoadingItemId(pivotId),
            groupId: pivotId === PivotIds.tags ? GroupIds.allTags : GroupIds.allBranches,
            type: ListBoxItemType.Loading,
            render: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IListBoxItem<{}>>, tableItem: IListBoxItem<{}>) => {
                return (
                    <LoadingCell
                        key={tableItem.id}
                        columnIndex={columnIndex}
                        tableColumn={tableColumn}
                        tableItem={tableItem}
                        onMount={() => {
                            const results = this.searchFunctionsByPivot[pivotId || PivotIds.branches](filterText, continuationToken);

                            const process = () => {
                                const listItemResults = this.processResolvedArtifacts(results.items.value, pivotId);
                                const pivot = this.pivots.find(pivot => pivot.id === pivotId);

                                if (pivot) {
                                    const items = pivot.items as GroupedItemProvider<IVersionDropdownItem>;
                                    this.processLoadedItems(items, listItemResults);

                                    // Remove the search loading item and add a new one if there's a continuation token.
                                    const loadingItemIndex = items.value.findIndex(item => item.id === pivotId + "-search-loading-item");
                                    if (loadingItemIndex > -1) {
                                        items.splice(loadingItemIndex, 1);
                                    }
                                    if (results.continuationToken) {
                                        items.push(this.getSearchLoadingItem(filterText, pivotId, results.continuationToken));
                                    } else {
                                        this.searching.value = false;
                                    }
                                }
                            };

                            if (!results.items.ready.value) {
                                const observer = (value: boolean) => {
                                    if (value) {
                                        process();
                                    }
                                };
                                this.addObserver(results.items.ready, observer);
                            } else {
                                process();
                            }
                        }}
                    />
                );
            }
        };
    }

    private getItemsForGroup(groupId: string): GroupedItemProvider<IVersionDropdownItem> | undefined {
        const pivotIndex = this.pivots.findIndex(pivot => pivot.id === this.groupToPivotMap[groupId]);
        if (pivotIndex > -1) {
            return this.pivots[pivotIndex].items as GroupedItemProvider<IVersionDropdownItem>;
        }
    }

    private getClearAction = () => {
        return this.versionDropdownOptions && this.versionDropdownOptions.viewClearButton
            ? (event: React.SyntheticEvent<HTMLElement>) => {
                  this.selection && this.selection.clear();
                  if (this.onSelect) {
                      this.onSelect(event, null);
                  }
              }
            : undefined;
    };

    private getBranchesActions = () => {
        return createActions(
            this.versionDropdownOptions.onCreateBranchClick,
            this.getClearAction(),
            this.selection.selectedCount === 0,
            this.versionDropdownOptions.otherBranchActions,
            this.isAuthorizedUser()
        );
    };

    private createPivots(
        versionDropdownOptions: IVersionDropdownOptions,
        groups: IListBoxGroup<IVersionDropdownItem>[],
        groupToPivotMap: { [groupId: string]: string }
    ): IDropdownPivot<IVersionDropdownItem>[] {
        const branchesPivot: IDropdownPivot<IVersionDropdownItem> = {
            id: PivotIds.branches,
            name: Resources.PivotBranches,
            actions: this.getBranchesActions(),
            filterPlaceholderText: Resources.BranchesSearchTextPlaceholder,
            filteredNoResultsText: Resources.BranchesSearchNoResultsText,
            showFilterBox: true,
            items: new GroupedItemProvider<IVersionDropdownItem>(
                [],
                groups.filter(group => groupToPivotMap[group.id] === PivotIds.branches)
            )
        };

        const tagsPivot: IDropdownPivot<IVersionDropdownItem> = {
            id: PivotIds.tags,
            name: Resources.PivotTags,
            actions: this.versionDropdownOptions.otherTagActions,
            filterPlaceholderText: Resources.TagsSearchTextPlaceholder,
            filteredNoResultsText: Resources.TagsSearchNoResultsText,
            noItemsText: Resources.TagsNoItemsText,
            showFilterBox: true,
            items: new GroupedItemProvider<IVersionDropdownItem>(
                [],
                groups.filter(group => groupToPivotMap[group.id] === PivotIds.tags)
            )
        };

        this.commitsItemProvider = new GroupedItemProvider<IVersionDropdownItem>(
            [],
            groups.filter(group => groupToPivotMap[group.id] === PivotIds.commits)
        );

        const commitsPivot: IDropdownPivot<IVersionDropdownItem> = {
            id: PivotIds.commits,
            name: Resources.PivotCommits,
            filterByText: false,
            filterPlaceholderText: Resources.CommitsSearchTextPlaceholder,
            filteredNoResultsText: new ObservableValue<string>(Resources.CommitsSearchMinimumCharactersError),
            noItemsText: Resources.CommitsSearchMinimumCharactersError,
            showFilterBox: true,
            items: this.commitsItemProvider
        };

        return [branchesPivot, this.viewTagsPivot && tagsPivot, versionDropdownOptions.viewCommitsPivot && commitsPivot].filter(
            Boolean
        ) as IDropdownPivot<IVersionDropdownItem>[];
    }

    private searchBranchRefs = (searchText: string, continuationToken?: string): PagedResults<IVersionDropdownItem> =>
        this.searchRefs(searchText, continuationToken, GroupIds.allBranches, this.getGitRefService().searchBranches);

    private searchTagRefs = (searchText: string, continuationToken?: string): PagedResults<IVersionDropdownItem> =>
        this.searchRefs(searchText, continuationToken, GroupIds.allTags, this.getGitRefService().searchTags);

    private searchRefs(
        searchText: string,
        continuationToken: string | undefined | null,
        groupKey: GroupIds,
        searchFunction: (repositoryId: string, projectId: string, searchText: string, continuationToken?: string | null) => PagedResults<GitRef>
    ): PagedResults<IVersionDropdownItem> {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.startScenario("branches-dropdown-search-refs", false);

        if (!continuationToken) {
            // If we're continuing a previous search, use given continuationToken.
            // Otherwise, it's the first search page, so don't fetch items that are already on the picker, let's steal the all's continuationToken.
            continuationToken = this.continuationTokens[groupKey];
        }

        const {
            id: repositoryId,
            project: { id: projectId }
        } = this.repository;

        const process = () => {
            items.push(...refs.items.value.map(ref => createVersionDropdownItem(ref, groupKey, this.repository, this.currentIdentityId, [])));
            performanceService.endScenario("Repos", "branches-dropdown-search-refs", false, undefined, {
                count: items.length,
                hasMore: Boolean(refs.continuationToken)
            });
            results.continuationToken = refs.continuationToken;
            items.ready.value = true;
        };

        const refs = searchFunction(repositoryId, projectId, searchText, continuationToken);
        const items = new ReadyableObservableArray<IVersionDropdownItem>();
        const results = { items, continuationToken: refs.continuationToken };
        if (refs.items.ready.value) {
            process();
        } else {
            this.addObserver(refs.items.ready, (value: boolean) => {
                if (value) {
                    process();
                }
            });
        }

        return results;
    }

    /**
     * Remember the last results, and if new text is strictly stronger,
     * filter in memory and avoid calling the expensive function.
     * Clarification: "123" is strictly stronger than "12" and than "23", but not than "13".
     * @param filterInMemory A function that filters results in memory.
     * @param canReuseResult A function to identify whether results were complete, or partial (so cannot reuse).
     */
    private reuseIfStrictlyStronger(fn: SearchItemsType, canReuseResult: (result: PagedResults<IVersionDropdownItem>) => boolean): SearchItemsType {
        let lastSearchText: string;
        let lastResults: PagedResults<IVersionDropdownItem>;

        return (searchText, continuationToken) => {
            const doSearch = () => {
                lastSearchText = searchText;
                lastResults = fn(searchText, continuationToken);
                return lastResults;
            };

            if (this.isStrictlyStronger(searchText, lastSearchText)) {
                const process = (result: PagedResults<IVersionDropdownItem>) => {
                    if (!result || !canReuseResult(result)) {
                        doSearch();
                    }
                };
                if (lastResults.items.ready.value) {
                    process(lastResults);
                } else {
                    this.addObserver(lastResults.items.ready, (value: boolean) => {
                        if (value) {
                            process(lastResults);
                        }
                    });
                }
                return lastResults;
            }

            return doSearch();
        };
    }

    /**
     * Call the passed in function with a delay.  Any calls to debounceFunction will cancel pending calls.
     */
    private debounceFunction<T extends Function>(fn: T, ms: number, ...args: any[]) {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        this.timeout = setTimeout(() => {
            this.timeout = undefined;
            fn.apply(null, args);
        }, ms);
    }

    private isStrictlyStronger(a: string, b: string): boolean {
        return Boolean(a && b && a.toLowerCase().indexOf(b.toLowerCase()) >= 0);
    }

    private isAuthorizedUser(): boolean {
        return userHasClaim(this.pageContext, UserClaims.Member);
    }
}

function createGroupToPivotMap(): { [id: string]: string } {
    const groupToPivotMap: { [id: string]: string } = {};
    groupToPivotMap[GroupIds.repositoryDefault] = PivotIds.branches;
    groupToPivotMap[GroupIds.mineBranches] = PivotIds.branches;
    groupToPivotMap[GroupIds.allBranches] = PivotIds.branches;
    groupToPivotMap[GroupIds.allTags] = PivotIds.tags;
    groupToPivotMap[GroupIds.foundCommits] = PivotIds.commits;
    return groupToPivotMap;
}

function createActions(
    onCreateBranchClick?: () => void,
    clearAction?: (event: React.SyntheticEvent<HTMLElement>) => void,
    clearActionDisabled?: boolean,
    otherActions?: IButtonProps[],
    isAuthorizedUser?: boolean
): IButtonProps[] {
    const actions: IButtonProps[] = [];

    if (onCreateBranchClick && isAuthorizedUser) {
        actions.push({
            text: Resources.NewBranch,
            iconProps: { iconName: "Add" },
            onClick: () => onCreateBranchClick()
        });
    }

    if (clearAction) {
        actions.push({
            text: Resources.ClearSelection,
            iconProps: { iconName: "Cancel" },
            disabled: clearActionDisabled,
            onClick: clearAction
        });
    }

    if (otherActions) {
        actions.push(...otherActions);
    }

    return actions;
}

function createVersionDropdownItem(
    version: GitRef | GitCommitRef,
    groupKey: string,
    repository: GitRepository,
    currentUserId: string,
    folderFavorites: string[]
): IVersionDropdownItem {
    if (groupKey === GroupIds.foundCommits) {
        version = version as GitCommitRef;
        return {
            friendlyName: RefUtils.getShortCommitId(version.commitId),
            versionDescriptor: {
                version: version.commitId,
                versionType: GitVersionType.Commit,
                versionOptions: GitVersionOptions.None
            },
            repositoryId: repository.id
        };
    } else {
        version = version as GitRef;
        const isTag = RefUtils.isTag(version.name);
        const friendlyName = RefUtils.getFriendlyName(version.name);

        return {
            friendlyName: friendlyName,
            versionDescriptor: {
                version: friendlyName,
                versionType: isTag ? GitVersionType.Tag : GitVersionType.Branch,
                versionOptions: GitVersionOptions.None
            },
            isMine: version.creator && version.creator.id === currentUserId,
            isDefault: version.name === repository.defaultBranch,
            isFavoritedByFolder: !isTag && belongsToFolderFavorite(version.name, folderFavorites),
            repositoryId: repository.id
        };
    }
}

function createAllBranchesVersionDropdownItem(repository: GitRepository): IVersionDropdownItem {
    return {
        friendlyName: "*",
        versionDescriptor: {
            version: "*",
            versionType: GitVersionType.Branch,
            versionOptions: GitVersionOptions.None
        },
        repositoryId: repository.id
    };
}

function belongsToFolderFavorite(refName: string, folderFavorites: string[]): boolean {
    return folderFavorites.some(folderFavorite => refName.startsWith(folderFavorite + "/"));
}

export function getItemFromVersion(version: GitVersionDescriptor, repository: GitRepository): IVersionDropdownItem {
    const isCommit = version.versionType === GitVersionType.Commit;
    let friendlyName = version.version;
    if (isCommit) {
        friendlyName = RefUtils.getShortCommitId(friendlyName);
    }

    const defaultBranch = RefUtils.getFriendlyName(repository.defaultBranch);
    return {
        versionDescriptor: version,
        friendlyName,
        isDefault: friendlyName === defaultBranch,
        repositoryId: repository.id
    };
}

function getFavoriteFromArtifact(item: IVersionDropdownItem): Favorite | undefined {
    const canBeFavorited = item.versionDescriptor.versionType === GitVersionType.Branch && !item.isDefault;
    if (!canBeFavorited) {
        return undefined;
    }

    return {
        artifactId: getVersionString(item.versionDescriptor),
        artifactName: item.friendlyName,
        artifactScope: {
            id: item.repositoryId,
            type: GitRefFavoriteArtifactConstants.artifactScopeType
        },
        artifactType: GitRefFavoriteArtifactConstants.artifactType
    } as Favorite;
}

function getArtifactListGroupId(item: IVersionDropdownItem): string {
    if (item.versionDescriptor.versionType === GitVersionType.Commit) {
        return GroupIds.foundCommits;
    } else if (item.versionDescriptor.versionType === GitVersionType.Tag) {
        return GroupIds.allTags;
    } else {
        return item.isDefault ? GroupIds.repositoryDefault : item.isMine || item.isFavoritedByFolder ? GroupIds.mineBranches : GroupIds.allBranches;
    }
}

function renderItemTextNode(item: IListBoxItem<IVersionDropdownItem>): JSX.Element | undefined {
    const data = item.data;
    if (!data) {
        return undefined;
    }
    const { prefix, lastName } =
        data.versionDescriptor.versionType === GitVersionType.Commit
            ? ({ lastName: item.text } as PrefixAndLastName)
            : splitVersionName(item.text || "");

    return (
        <span>
            {prefix && <span className="version-dropdown-prefix-name">{prefix}</span>}
            {lastName}
            {data.isDefault && <span className="version-dropdown-default-badge">{Resources.DefaultBadge}</span>}
        </span>
    );
}

function splitVersionName(name: string): PrefixAndLastName {
    const slashIndex = name.lastIndexOf("/");
    return slashIndex > 0 ? { prefix: name.substring(0, slashIndex + 1), lastName: name.substring(slashIndex + 1) } : { lastName: name };
}

function areRefsResultsComplete(list: PagedResults<IVersionDropdownItem>): boolean {
    return !list.continuationToken;
}
